/**
 * Реплика — единственный источник данных для экранов (CC §4.1 п. 2),
 * поэтому `Me` в состоянии приложения и зеркало для service worker'а
 * обновляются ОТ РЕПЛИКИ, а не от конкретного ответа сервера.
 *
 * Это закрывает случай, когда настройки изменили на другом устройстве:
 * приходит `me.updated`, реплика обновляется, и вслед за ней — заголовок
 * `Accept-Language`, интерфейс и запись `notif_preview` в `app-v1-device`
 * (CLIENT_WEB.md §8.3 п. 4).
 */
import { liveQuery, type Subscription } from 'dexie'

import { setApiLocale } from '@shared/api/client'
import type { Me } from '@shared/api/types'
import { meRepo, mirrorSettingsForWorker } from '@shared/db'

import { useAppStore } from '../model/app-store'
import { applyDocumentLanguage } from '../model/locale'

let subscription: Subscription | null = null
let lastSignature = ''

export function startMeMirror(userId: string): void {
  stopMeMirror()

  subscription = liveQuery(() => meRepo.get(userId)).subscribe({
    next: (record) => {
      if (!record) return
      void applyMe(record)
    },
    error: () => {
      // База закрыта на смене пользователя — подписка просто прекращается.
    },
  })
}

export function stopMeMirror(): void {
  subscription?.unsubscribe()
  subscription = null
  lastSignature = ''
}

async function applyMe(me: Me): Promise<void> {
  const store = useAppStore.getState()
  store.setMe(me)

  const locale = me.settings.locale || 'ru'
  const signature = `${locale}|${String(me.settings.notif_preview)}`
  if (signature === lastSignature) return
  lastSignature = signature

  setApiLocale(locale)
  applyDocumentLanguage(locale)
  await mirrorSettingsForWorker(locale, me.settings.notif_preview)
}
